import { getPinyinMp3Asset, hasPinyinMp3Asset } from "@-/dev.improve.haniverse.assets.pinyin.audio";
import { usePrevalentPinyin } from "@-/dev.improve.haniverse.routes.nav.app._lib.morpheme.grapheme.pinyin.prevalence";
import { useCallback, useMemo, useRef } from "react";
import {
  playPronunciationSequence,
  playPronunciationTrack,
  type PronunciationAudioTrack,
} from "#shared-pronunciation-player";

type PinyinAudioCard = {
  pinyinList: readonly { toneNumbersPinyin: string }[];
} & ({ grapheme: string } | { front: string });

function pinyinAudioCardGrapheme(card: PinyinAudioCard): string {
  return "grapheme" in card ? card.grapheme : card.front;
}

function resolveCardPinyinKey(
  pinyinList: readonly { toneNumbersPinyin: string }[],
  selectedPinyin?: string | null,
): string | undefined {
  return selectedPinyin ?? pinyinList[0]?.toneNumbersPinyin;
}

/**
 * Whether a card has bundled audio. Cheap enough to call during render — it only
 * checks the pinyin-key set and never requires an mp3 asset.
 */
export function cardPinyinHasAudio(
  pinyinList: readonly { toneNumbersPinyin: string }[],
  selectedPinyin?: string | null,
): boolean {
  const key = resolveCardPinyinKey(pinyinList, selectedPinyin);
  return key !== undefined && hasPinyinMp3Asset(key);
}

/**
 * Resolve the bundled audio track for a card's pinyin, requiring the
 * underlying mp3 module. Its source is a numeric AssetRegistry id on native and a
 * URL string on web. Call lazily (at play time) — not during render of a list.
 */
export function resolveCardPinyinAudioTrack(
  pinyinList: readonly { toneNumbersPinyin: string }[],
  selectedPinyin?: string | null,
): PronunciationAudioTrack | null {
  const key = resolveCardPinyinKey(pinyinList, selectedPinyin);
  if (key === undefined) {
    return null;
  }
  const source = getPinyinMp3Asset(key);
  return source === null ? null : { pinyin: key, source };
}

/** Every study pinyin that has a recording, in order, for a sequence. */
export function resolveCardPinyinAudioTracks(
  pinyinList: readonly { toneNumbersPinyin: string }[],
): PronunciationAudioTrack[] {
  const tracks: PronunciationAudioTrack[] = [];
  for (const entry of pinyinList) {
    const source = getPinyinMp3Asset(entry.toneNumbersPinyin);
    if (source !== null) {
      tracks.push({ pinyin: entry.toneNumbersPinyin, source });
    }
  }
  return tracks;
}

/**
 * Press-only pinyin-audio playback used by `PinyinSoundButton`. Returns
 * callbacks the button invokes from `onPress` handlers. This hook MUST NOT be
 * called from a `useEffect` — programmatic playback lives in
 * `routes/nav/app/learn/_lib/audio` and is gated to the learn layout.
 */
export function useGraphemePinyinAudio(
  graphemeCard: PinyinAudioCard,
  selectedPinyin: string | null | undefined,
  options: { isMutedOverride: boolean },
) {
  const { isMutedOverride } = options;
  const studyPinyinList = usePrevalentPinyin(
    pinyinAudioCardGrapheme(graphemeCard),
    graphemeCard.pinyinList,
  );
  const hasAudio = useMemo(
    () => cardPinyinHasAudio(studyPinyinList, selectedPinyin),
    [selectedPinyin, studyPinyinList],
  );

  // Read the latest mute state without re-creating the play callbacks — the
  // shared player samples it at call time.
  const isMutedRef = useRef(isMutedOverride);
  isMutedRef.current = isMutedOverride;
  const getIsMuted = useCallback(() => isMutedRef.current, []);

  const play = useCallback(async () => {
    const track = resolveCardPinyinAudioTrack(studyPinyinList, selectedPinyin);
    if (track === null) {
      return;
    }
    await playPronunciationTrack(track, getIsMuted);
  }, [selectedPinyin, studyPinyinList, getIsMuted]);

  const playSequence = useCallback(async () => {
    await playPronunciationSequence(resolveCardPinyinAudioTracks(studyPinyinList), getIsMuted);
  }, [studyPinyinList, getIsMuted]);

  return { hasAudio, play, playSequence };
}
